// Where a party can move to (the Move table dialog): every other table, sorted into the ones
// that will take them and the ones that won't, with why. Pure, so the rules are tested.
//
// A table takes them if it's free, seats them, and isn't held for a booking arriving within
// the hour (holds.py marks those `reserved`). A free table booked later tonight still takes
// them, with a warning when that booking is due before they'd be gone.

const hhmm = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
const LIVE = new Set(['pending', 'confirmed', 'arrived']);

/** Minutes the party at `table` has left, from when it sat and its allowance. */
export function minutesLeft(table, now = new Date()) {
  const limit = Number(table?.time_limit_minutes) || 90;
  if (!table?.opened_at) return limit;
  const used = Math.floor((now.getTime() - new Date(table.opened_at).getTime()) / 60000);
  return Math.max(0, limit - used);
}

/** `{ free: [...], taken: [...], best }`, each entry `{ table, note, warn?, why? }`. */
export function moveOptions(source, tables, bookings = [], now = new Date()) {
  const covers = Number(source?.covers) || 1;
  const nowStr = hhmm(now);
  const leaveBy = hhmm(new Date(now.getTime() + minutesLeft(source, now) * 60000));
  const live = (bookings || []).filter((b) => LIVE.has(b.status));
  const nextFor = (t) => live
    .filter((b) => b.table_id === t.id && (b.time || '') >= nowStr)
    .sort((a, b) => (a.time || '').localeCompare(b.time || ''))[0];
  const byId = Object.fromEntries(live.map((b) => [b.id, b]));

  const free = [];
  const taken = [];
  for (const t of tables || []) {
    if (!t || t.id === source?.id) continue;
    const status = String(t.status || '').toLowerCase();
    if (status === 'occupied') { taken.push({ table: t, why: 'Has guests' }); continue; }
    if (status === 'cleaning') { taken.push({ table: t, why: 'Being cleared' }); continue; }
    if (status === 'reserved') {
      const b = byId[t.reservation_id];
      taken.push({ table: t, why: b ? `Held for ${b.customer_name}, ${b.time}` : 'Held for a booking' });
      continue;
    }
    if ((Number(t.capacity) || 0) < covers) {
      taken.push({ table: t, why: `Seats ${t.capacity}: too small for ${covers}` });
      continue;
    }
    const next = nextFor(t);
    if (next && next.time < leaveBy) {
      free.push({ table: t, warn: true, note: `Booked ${next.time}, ${next.customer_name} (${next.party_size})`, booking: next });
    } else if (next) {
      free.push({ table: t, note: `Free until ${next.time}` });
    } else {
      free.push({ table: t, note: 'Free all evening' });
    }
  }
  // The best fit: no clash, the fewest spare seats, then the floor's own order.
  const order = new Map((tables || []).map((t, i) => [t.id, i]));
  free.sort((a, b) => (a.warn === b.warn ? 0 : a.warn ? 1 : -1)
    || (a.table.capacity - b.table.capacity)
    || (order.get(a.table.id) - order.get(b.table.id)));
  const best = free.find((o) => !o.warn)?.table?.id || null;
  return { free, taken, best };
}
